import {
  getAccessToken,
  refreshAccessToken
} from '@/app/common/utils/customFetchBase';
import { getMcpOAuthBase } from '@/app/common/api/config';
import { toMcpApiErrorMessage } from './mcpApi';

export type McpOAuthConsentComplete = {
  redirectTo: string;
};

export type McpOAuthSession = {
  id: string;
  claimedName: string;
  redirectScheme: string;
  redirectHost: string;
  redirectUri: string;
  createdAt: number;
};

function unwrap<T>(json: unknown): T {
  if (
    json &&
    typeof json === 'object' &&
    'data' in json &&
    (json as { data?: T }).data !== undefined
  ) {
    return (json as { data: T }).data;
  }
  return json as T;
}

async function mcpOAuthFetch<T>(
  path: string,
  init: RequestInit,
  retried = false
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${getMcpOAuthBase()}${path}`, {
    ...init,
    headers
  });
  const text = await response.text().catch(() => '');
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = text;
    }
  }

  if (response.status === 401 && !retried) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return mcpOAuthFetch<T>(path, init, true);
  }

  if (!response.ok) {
    const message =
      json && typeof json === 'object' && 'msg' in json
        ? String((json as { msg?: unknown }).msg ?? '')
        : text;
    throw new Error(message || `HTTP ${response.status}`);
  }

  return unwrap<T>(json);
}

export async function completeMcpOAuthConsent(
  requestId: string,
  code: string
): Promise<McpOAuthConsentComplete> {
  return mcpOAuthFetch<McpOAuthConsentComplete>(
    `/consent/${encodeURIComponent(requestId)}`,
    {
      method: 'POST',
      body: JSON.stringify({ code })
    }
  );
}

export async function listMcpOAuthSessions(): Promise<McpOAuthSession[]> {
  const result = await mcpOAuthFetch<{ sessions: McpOAuthSession[] }>(
    '/sessions',
    { method: 'GET' }
  );
  return result.sessions ?? [];
}

export async function revokeMcpOAuthSession(id: string): Promise<void> {
  await mcpOAuthFetch<{ ok: boolean }>(
    `/sessions/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
}

export { toMcpApiErrorMessage };
